import React, { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Book } from '@/utils/bookRouting';
import type { BookVisibilityTrigger } from '@/services/bookAnalytics';
import { formatTimeLeft } from '@/utils/timeUtils';
import AnimatedBookCover from './AnimatedBookCover';
import AnimatedPlayIcon from './AnimatedPlayIcon';
import BookRating from './BookRating';
import BookActionsMenu from './BookActionsMenu';
import PauseIcon from './icons/PauseIcon';
import PlayIcon from './icons/PlayIcon';

interface MyBookItemProps {
  book: Book;
  isCurrentBook: boolean;
  isPlaying: boolean;
  isReady: boolean;
  isPositionRestored: boolean;
  currentTime: number;
  savedTime: number;
  cachedTime?: number;
  isDarkMode: boolean;
  breakpoint: string;
  isFav: boolean;
  currentRating: number;
  menuIsOpen: boolean;
  removing: boolean;
  isFilterTransitioning: boolean;
  onBookSelect: (slug: string) => void;
  onTogglePlay: () => void;
  onStartReading: (slug: string) => void;
  onPreloadBook: (bookId: string) => void;
  toggleMenu: (id: string) => void;
  toggleFavourite: (id: string) => void;
  closeMenu: () => void;
  handleMarkFinished: (book: Book) => void;
  handleMarkUnfinished: (book: Book) => void;
  handleShowConversations: (id: string) => void;
  resetProgressForPlayback: (book: Book) => void;
  isHidden: boolean;
  toggleHidden: (id: string, trigger: BookVisibilityTrigger) => void;
}

const MyBookItem = React.memo<MyBookItemProps>(({
  book,
  isCurrentBook,
  isPlaying,
  isReady,
  isPositionRestored,
  currentTime,
  savedTime,
  cachedTime,
  isDarkMode,
  breakpoint,
  isFav,
  currentRating,
  menuIsOpen,
  removing,
  isFilterTransitioning,
  onBookSelect,
  onTogglePlay,
  onStartReading,
  onPreloadBook,
  toggleMenu,
  toggleFavourite,
  closeMenu,
  handleMarkFinished,
  handleMarkUnfinished,
  handleShowConversations,
  resetProgressForPlayback,
  isHidden,
  toggleHidden,
}) => {
  const effectiveTime = useMemo(() => {
    if (isCurrentBook && currentTime > 0 && isReady && isPositionRestored) return currentTime;
    if (cachedTime !== undefined) return cachedTime;
    return savedTime;
  }, [isCurrentBook, currentTime, isReady, isPositionRestored, cachedTime, savedTime]);

  const progressPercent = Math.min(100, Math.max(0, (effectiveTime / book.totalDuration) * 100));
  const isBookPlaying = isCurrentBook && isPlaying;
  const isFinished = effectiveTime >= book.totalDuration - 2;
  const isUnstarted = effectiveTime === 0;
  const handlePreload = useCallback(() => {
    if (!isCurrentBook) {
      onPreloadBook(book.id);
    }
  }, [book.id, isCurrentBook, onPreloadBook]);

  const handleToggleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleMenu(book.id);
  }, [book.id, toggleMenu]);

  const handleFavouriteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavourite(book.id);
    closeMenu();
  }, [book.id, toggleFavourite, closeMenu]);

  const handleMarkFinishedClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleMarkFinished(book);
    closeMenu();
  }, [book, handleMarkFinished, closeMenu]);

  const handleMarkUnfinishedClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleMarkUnfinished(book);
    closeMenu();
  }, [book, handleMarkUnfinished, closeMenu]);

  const handleShowConversationsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleShowConversations(book.id);
    closeMenu();
  }, [book.id, handleShowConversations, closeMenu]);

  const handleToggleHiddenClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleHidden(book.id, 'visibility_toggle');
    closeMenu();
  }, [book.id, toggleHidden, closeMenu]);

  const iconSize = ['base', '3xs'].includes(breakpoint) ? 'xs' : 'sm';

  return (
    <motion.div
      layout={isFilterTransitioning ? false : "position"}
      initial={isFilterTransitioning ? false : { opacity: 0, y: 10, scale: 0.99 }}
      animate={removing ? { opacity: 0, scale: 0.95 } : { opacity: 1, y: 0, scale: 1 }}
      exit={isFilterTransitioning ? { opacity: 1, transition: { duration: 0 } } : { opacity: 0, y: -10, scale: 0.98 }}
      transition={isFilterTransitioning ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
      className={`
        relative flex flex-row gap-3.5 2xs:gap-5 xs:gap-5 sm:gap-5.5 py-4 pr-2 2xs:py-5 sm:py-6 lg:py-7 h-28 2xs:h-32 sm:h-55 lg:h-67
        transition-colors duration-200
        ${isDarkMode
          ? 'hover:bg-gray-800/20'
          : 'hover:bg-gray-50/60'}
      `}
    >
      {/* Book Cover */}
      <AnimatedBookCover
        coverImageUrl={book.coverUrl || ''}
        coverAnimationUrl={book.coverAnimationUrl}
        title={book.title}
        author={book.author}
        containerClassName="relative h-full aspect-square overflow-hidden cursor-pointer self-start"
        imageClassName="absolute inset-0 w-full h-full object-cover rounded-lg shadow-md"
        videoClassName="absolute inset-0 w-full h-full object-cover rounded-lg shadow-md"
        imageStyle={{ objectPosition: 'bottom' }}
        videoStyle={{ objectPosition: 'bottom' }}
        onClick={() => onBookSelect(book.slug)}
      />

      {/* Book details */}
      <div className="flex-1 flex flex-col justify-center sm:justify-start min-w-0 h-full sm:h-auto">
        <div>
          <div className="flex justify-between items-start mb-1 2xs:mb-2">
            <div>
              <h2
                role="button"
                className={`cursor-pointer portrait-text-app text-sm 2xs:text-base sm:text-xl lg:text-2xl font-medium pb-1 line-clamp-3 sm:line-clamp-1 ${
                  isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light-light'}`}
                onClick={() => onBookSelect(book.slug)}
              >
                {book.title}
              </h2>
              <p className={`text-xs sm:text-sm lg:text-base line-clamp-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                by {book.author}
              </p>
            </div>

            {/* Desktop menu */}
            <div className="hidden sm:block">
              <BookActionsMenu
                isDarkMode={isDarkMode}
                isOpen={menuIsOpen}
                onToggleOpen={handleToggleOpen}
                onFavourite={handleFavouriteClick}
                onMarkFinished={handleMarkFinishedClick}
                onMarkUnfinished={handleMarkUnfinishedClick}
                onShowConversations={handleShowConversationsClick}
                onToggleHidden={handleToggleHiddenClick}
                isFav={isFav}
                isFinished={isFinished}
                isUnstarted={isUnstarted}
                isHidden={isHidden}
              />
            </div>
          </div>

          <p
            className={`max-h-0 overflow-hidden sm:max-h-32 text-sm xs:line-clamp-1 lg:line-clamp-3 sm:mb-3.5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {book.description}
          </p>

          {/* Desktop progress bar */}
          <div className='hidden sm:block sm:mb-4 md:mb-4 lg:mb-3.5'>
            <div
              className={`w-full h-2 rounded-full overflow-hidden mb-1.5 ${
                isDarkMode ? 'bg-gray-700' : 'bg-blue-200'
              }`}
            >
              <div
                className={`h-full bg-blue-600 ${isBookPlaying ? '' : 'transition-all duration-300'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div
              className={`flex justify-between text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <span>{Math.floor(progressPercent)}% complete</span>
              <span>{formatTimeLeft(book.totalDuration, effectiveTime)}</span>
            </div>
          </div>

          {/* Desktop action row */}
          <div className="hidden sm:flex items-center justify-between">
            <BookRating
              book={book}
              progressSeconds={effectiveTime}
              initialRating={currentRating}
              isDarkMode={isDarkMode}
            />

            <div className="flex items-center gap-4">
              <button
                onPointerEnter={handlePreload}
                onPointerDown={handlePreload}
                onTouchStart={handlePreload}
                onFocus={handlePreload}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isHidden) {
                    toggleHidden(book.id, 'playback_start');
                  }
                  if (isFinished) {
                    resetProgressForPlayback(book);
                  }
                  if (isCurrentBook) {
                    onTogglePlay();
                  } else {
                    onStartReading(book.slug);
                  }
                }}
                className="px-4 py-2 min-w-25 bg-blue-600 text-white text-sm rounded-full cursor-pointer hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
              >
                {isCurrentBook && isPlaying ? (
                  <>
                    <PauseIcon size="xs" />
                    <span>Pause</span>
                  </>
                ) : (
                  <>
                    <PlayIcon size="xs" />
                    <span>Play</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile progress bar */}
        <div className="sm:hidden mr-1 mt-0.5">
          <div className="flex items-center justify-between gap-4 max-w-54">
            <div
              className={`hidden 2xs:block flex-1 h-2 rounded-full overflow-hidden ${
                isDarkMode ? "bg-gray-700" : "bg-blue-200"
              }`}
            >
              <div
                className={`h-full bg-blue-600 ${isBookPlaying ? '' : 'transition-all duration-300'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <span
              className={`text-xs whitespace-nowrap ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {formatTimeLeft(book.totalDuration, effectiveTime)}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Play/Pause and Menu */}
      <div className="flex sm:hidden flex-row items-center gap-2 2xs:gap-4">
        <button
          onPointerEnter={handlePreload}
          onPointerDown={handlePreload}
          onTouchStart={handlePreload}
          onFocus={handlePreload}
          onClick={(e) => {
            e.stopPropagation();
            if (isHidden) {
              toggleHidden(book.id, 'playback_start');
            }
            if (isFinished) {
              resetProgressForPlayback(book);
            }
            if (isCurrentBook) onTogglePlay();
            else onStartReading(book.slug);
          }}
          className="w-10 h-10 2xs:w-12 2xs:h-12 flex justify-center items-center rounded-full cursor-pointer bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        >
          <AnimatedPlayIcon
            isPlaying={isBookPlaying}
            showStopIcon={false}
            size={iconSize}
            playIconClassName="ml-[2px]"
          />
        </button>

        <BookActionsMenu
          isDarkMode={isDarkMode}
          isOpen={menuIsOpen}
          onToggleOpen={handleToggleOpen}
          onFavourite={handleFavouriteClick}
          onMarkFinished={handleMarkFinishedClick}
          onMarkUnfinished={handleMarkUnfinishedClick}
          onShowConversations={handleShowConversationsClick}
          onToggleHidden={handleToggleHiddenClick}
          isFav={isFav}
          isFinished={isFinished}
          isUnstarted={isUnstarted}
          isHidden={isHidden}
        />
      </div>
    </motion.div>
  );
});

export default MyBookItem;
